// Global Instructions Rule Applied!
//
// The fund picker for InputManager-rendered fields.
//
// core/components/FundSelect owns the gate, the pin and the "show all" footer
// for a plain antd Select — but an InputManager field is rendered by
// SelectInput, so a call site there cannot use it. This hook returns the same
// two things in the shape a SelectFieldConfig wants: gated `options`, and an
// `optionsFooter` node for the one-prop widening on SelectFieldConfig.
//
// Keep the two in step: a change to what the footer says belongs in both.

import React from 'react'
import { Divider } from 'antd'
import { useFundOptions } from './useFundOptions.ts'
import type { FundOptionsGate } from '../fundOptions.ts'
import type { SelectListEntry } from '../../../state_manager/data_models/select_list_entry.ts'

export interface UseFundFieldOptionsResult {
  options: SelectListEntry[]
  /** Pass to SelectFieldConfig.optionsFooter. Null when nothing is hidden, so
   *  a firm with no closed funds never sees a pointless link. */
  optionsFooter: React.ReactNode | null
}

export function useFundFieldOptions(gate: FundOptionsGate = {}): UseFundFieldOptionsResult {
  const { options, showAll, setShowAll, hiddenCount } = useFundOptions(gate)

  const optionsFooter =
    hiddenCount > 0 ? (
      <>
        <Divider style={{ margin: '4px 0' }} />
        <div className='px-2 pb-1'>
          <button
            type='button'
            data-testid='fund-select-show-all'
            className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left text-xs'
            // onMouseDown + preventDefault: a plain onClick closes the dropdown
            // before the state lands, so it never reopens with the extra funds.
            onMouseDown={(e) => {
              e.preventDefault()
              setShowAll(!showAll)
            }}
          >
            {showAll ? 'Show active funds only' : `Show all funds (${hiddenCount} closed)`}
          </button>
        </div>
      </>
    ) : null

  return {
    options: options.map((o) => ({ value: o.value, label: o.label })) as SelectListEntry[],
    optionsFooter
  }
}
